import { useMemo, useState, useEffect } from 'react'
import { questions } from '../data/questions'
import { examBlueprints } from '../data/examBlueprints'
import type { Attempt, AttemptUpdate, Exam as E } from '../types'
import { QuestionCard } from '../components/QuestionCard'
import { createTsaMathPilot, downloadTsaMathPilot, getTsaMathCoverage, TSA_MATH_PILOT } from '../data/tsaMathExam'

const newSeed = () => typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export function Exam({ addAttempt, updateAttempt, go }: { attempts: Attempt[]; addAttempt: (attempt: Attempt) => void; updateAttempt: (id: string, update: AttemptUpdate) => void; go?: (route: string) => void }) {
  const [exam, setExam] = useState<E>('HSA')
  const [n, setN] = useState(0)
  const [tsaSeed, setTsaSeed] = useState(newSeed)
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<string>>(new Set())
  const [controlsCollapsed, setControlsCollapsed] = useState(true)
  const blueprint = examBlueprints[exam]
  const duration = exam === 'TSA' ? TSA_MATH_PILOT.minutes : blueprint.miniMock.minutes
  const [timeLeft, setTimeLeft] = useState(examBlueprints.HSA.miniMock.minutes * 60)
  
  const list = useMemo(() => {
    const pool = questions.filter(question => question.exam === exam)
    if (exam === 'TSA') return createTsaMathPilot(pool, tsaSeed)
    if (!blueprint.miniMockQuestionIds?.length) return pool.slice(0, blueprint.miniMock.questions)
    const byId = new Map(pool.map(question => [question.id, question]))
    return blueprint.miniMockQuestionIds.map(id => byId.get(id)).filter((question): question is NonNullable<typeof question> => Boolean(question))
  }, [exam, blueprint.miniMock.questions, blueprint.miniMockQuestionIds, tsaSeed])
  const q = list[n]
  const isInProgress = Boolean(q)
  const tsaCoverage = useMemo(() => exam === 'TSA' ? getTsaMathCoverage(list) : [], [exam, list])

  useEffect(() => {
    if (!q) return
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0)
    }, 1000)
    return () => clearInterval(timer)
  }, [q])

  // The mini mock is deliberately a focused mode. Native navigation and
  // reloads must ask first so students do not lose their current position.
  useEffect(() => {
    if (!isInProgress) return
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [isInProgress])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  if (!q) {
    return (
      <div className="container mt-8">
        <div className="card text-center">
          <div className="feature-icon mx-auto mb-4">🏆</div>
          <h2>Hoàn thành {exam === 'TSA' ? 'TSA Toán chuyên sâu (Pilot)' : `Mini Test ${exam}`}</h2>
          <p className="mb-8">Bạn đã xuất sắc vượt qua bài thi thử với cấu trúc mô phỏng.</p>
          <button className="btn btn-primary" onClick={() => { if (exam === 'TSA') setTsaSeed(newSeed()); setFlaggedQuestions(new Set()); setN(0); setTimeLeft(duration * 60) }}>
            Làm lại bài thi 🔄
          </button>
        </div>
      </div>
    )
  }

  const isWarning = timeLeft < 120 // less than 2 mins
  const leaveExam = () => {
    if (!isInProgress || window.confirm('Bạn đang làm bài. Rời trang bây giờ sẽ kết thúc lượt thi hiện tại.')) go?.('home')
  }
  const submitExam = () => {
    if (window.confirm('Nộp bài ngay? Bạn sẽ không thể quay lại lượt thi này.')) setN(list.length)
  }

  return (
    <div className={['exam-layout', 'exam-workspace', controlsCollapsed ? 'exam-controls-collapsed' : 'exam-controls-open'].join(' ')}>
      <aside className="exam-control-rail" aria-label="Điều khiển bài thi">
        <button
          type="button"
          className="exam-control-toggle"
          onClick={() => setControlsCollapsed(current => !current)}
          aria-expanded={!controlsCollapsed}
          aria-label={controlsCollapsed ? 'Mở điều khiển bài thi' : 'Thu gọn điều khiển bài thi'}
          title={controlsCollapsed ? 'Mở điều khiển' : 'Thu gọn điều khiển'}
        >
          {controlsCollapsed ? '›' : '‹'}
        </button>
        {!controlsCollapsed && (
          <div className="exam-control-panel">
            <span className="sidebar-label">Điều khiển bài thi</span>
            <button className="btn btn-ghost exam-control-home" onClick={leaveExam}>← Trang chủ</button>
            <label className="sidebar-section">
              <span className="sidebar-label">Loại đề</span>
              <select
                aria-label="Loại đề thi"
                value={exam}
                onChange={e => { const nextExam = e.target.value as E; setExam(nextExam); setN(0); setFlaggedQuestions(new Set()); setTimeLeft((nextExam === 'TSA' ? TSA_MATH_PILOT.minutes : examBlueprints[nextExam].miniMock.minutes) * 60); if (nextExam === 'TSA') setTsaSeed(newSeed()) }}
              >
                {Object.values(examBlueprints).map(item => <option key={item.code} value={item.code} disabled={!item.enabled}>{item.label}</option>)}
              </select>
            </label>
            <button className="btn btn-primary" onClick={submitExam}>Nộp bài</button>
            <p className="text-muted text-sm">Trình duyệt sẽ hỏi trước khi tải lại hoặc rời bài.</p>
          </div>
        )}
      </aside>
      <div className="question-area">
        <div className="exam-utility-bar">
          <button className="exam-home-button" onClick={leaveExam}>← Trang chủ</button>
          <span>Đang làm bài · mọi câu trả lời đã chọn được lưu vào kết quả</span>
          <button className="exam-submit-link" onClick={submitExam}>Nộp bài</button>
        </div>
        <div className="flex justify-between items-center mb-4 pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3">
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>{exam === 'TSA' ? 'TSA Toán chuyên sâu (Pilot)' : `Thi thử (${exam})`}</h3>
            <span className="tag-pill orange">Câu hỏi {n + 1} / {list.length}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Timer badge */}
            <div 
              style={{ 
                padding: '0.4rem 0.9rem', 
                borderRadius: '8px', 
                background: isWarning ? '#fef2f2' : '#f8fafc',
                color: isWarning ? 'var(--error)' : 'var(--orange-primary)',
                border: `1.5px solid ${isWarning ? 'var(--error)' : 'var(--orange-primary)'}`,
                fontWeight: 800,
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              ⏱️ {formatTime(timeLeft)}
            </div>

            <select
              className="exam-header-select"
              value={exam} 
              onChange={e => { const nextExam = e.target.value as E; setExam(nextExam); setN(0); setFlaggedQuestions(new Set()); setTimeLeft((nextExam === 'TSA' ? TSA_MATH_PILOT.minutes : examBlueprints[nextExam].miniMock.minutes) * 60); if (nextExam === 'TSA') setTsaSeed(newSeed()) }}
              style={{ 
                padding: '0.4rem 0.8rem', 
                borderRadius: '8px', 
                background: '#ffffff', 
                color: 'var(--text-primary)',
                border: '1.5px solid var(--border-color)',
                fontWeight: 600,
                fontSize: '0.9rem',
                outline: 'none'
              }}
            >
              {Object.values(examBlueprints).map(item => <option key={item.code} value={item.code} disabled={!item.enabled}>{item.enabled ? item.label : `${item.label} — chưa có kho đề hợp pháp`}</option>)}
            </select>
          </div>
        </div>
        {exam === 'TSA' && (
          <div className="tsa-coverage-strip" aria-label="Phủ ma trận đề TSA pilot">
            {tsaCoverage.map(item => <span key={item.key} className={`tag-pill ${item.count ? 'green' : 'orange'}`}>{item.label}: {item.count}</span>)}
          </div>
        )}
        
        <QuestionCard 
          key={q.id} 
          q={q} 
          session="exam"
          onDone={addAttempt}
          onReflect={(id, misconception) => updateAttempt(id, { misconception })}
          onContinue={() => setN(x => x + 1)}
          flagged={flaggedQuestions.has(q.id)}
          onToggleFlag={(questionId, shouldFlag) => setFlaggedQuestions(previous => {
            const next = new Set(previous)
            if (shouldFlag) next.add(questionId)
            else next.delete(questionId)
            return next
          })}
        />
      </div>

      {/* Floating Sticky Bottom Progress Bar */}
      <div className="sticky-bottom-bar-wrapper">
        <div className="sticky-bottom-bar">
          <div className="flex items-center gap-2 flex-shrink-0">
            {exam === 'TSA' && <button
              className="btn btn-secondary"
              style={{ padding: '0.35rem 0.85rem', fontSize: '0.8rem', borderRadius: '8px' }}
              onClick={() => downloadTsaMathPilot(list, tsaSeed)}
            >
              Xuất đề
            </button>}
            <span style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              Câu {n + 1}/{list.length}
            </span>
          </div>

          <div className="q-grid-horizontal">
            {list.map((_, i) => {
              let className = "q-badge"
              if (i === n) className += " active"
              else if (i < n) className += " answered"
              if (flaggedQuestions.has(list[i].id)) className += " flagged"
              
              return (
                <button 
                  key={i} 
                  className={className}
                  onClick={() => setN(i)}
                  title={`Chuyển đến câu ${i + 1}`}
                >
                  {flaggedQuestions.has(list[i].id) ? '🚩' : i + 1}
                </button>
              )
            })}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="btn btn-secondary"
              style={{ padding: '0.35rem 0.85rem', fontSize: '0.8rem', borderRadius: '8px' }}
              onClick={submitExam}
            >
              Nộp bài sớm
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
